import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { SideForm } from '../../components/SideForms/SideForm';

import {
  Button,
  TextInput,
  Form,
  FormGroup,
  TextInputSkeleton,
  TextAreaSkeleton,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Link,
  Row,
  Column,
  Toggle,
  Loading,
  InlineLoading,
} from '@carbon/react';

import SingleLocationMap from '../../components/Map/SingleLocationMap';
import { hideForm } from 'services/forms';
import { useLazyGetLocationQuery, useUpdateLocationMutation } from 'services/location';
import { showToast } from 'services/toasts';
import LoadingPage from '@/pages/LoadingPage';
import useUser from '@/lib/hooks/useUser';
import { useConfirmClosingForm } from '@/lib/modal';
import useModal from '@/lib/hooks/useModal';
import CustomSelect from '@/components/SelectComponent';
import { useGetContactsQuery } from '@/redux/new_services/contacts';

const defaultItem = {
  name: '',
  address: {
    street: '',
    postcode: '',
    city: '',
  },
};

const has_error = (error_code, error_state = {}, error_type = '') => {
  if (error_state.data) {
    if (error_state.data.msg && error_state.data.code) {
      const { code, msg, type } = error_state.data;
      if (code === error_code) {
        if (error_type === '' && type === '') {
          return msg;
        }

        if (error_type === type) {
          return msg;
        }
        return null;
      }
    }
  }
  return null;
};

const LocationForm = ({
  id = null,
  isNew = false,
}) => {

  const sideform_ref = useRef(null);

  const dispatch = useDispatch();
  const { isAdmin } = useUser();

  const confirmClosingUnsavedForm = useConfirmClosingForm();
  const [contactForm, hideContactForm] = useModal('CONTACTS_FORM');
  const [confirmRemoveLocation] = useModal('DELETE_LOCATION');

  const [item, setItem] = useState(null);

  const [unsaved, setUnsaved] = useState(false);

  const {data: contacts, isLoading: loadingContacts} = useGetContactsQuery();

  const [
    trigger,
    {
      isLoading,
      error: loadingError,
    }
  ] = useLazyGetLocationQuery({refetchOnMountOrArgChange: true});

  const [
    updateLocation,
    {
      isLoading: isSaving,
      error,
    }
  ] = useUpdateLocationMutation();

  const load = useCallback((id) => {
    console.info("load location", id);
    trigger(id)
      .unwrap()
      .then(data => {
        setItem(data.data);
      }).catch((error) => {
        console.error("error on loading location:", error);
        dispatch(hideForm());
      })
  }, [trigger, dispatch]);

  //const hasItemChanged = useHasChanged(item);
  //const hasIsSavingChanged = useHasChanged(is_saving);

  // init / loading
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {

    if (!isNew) {
      load(id);
    } else {
      setItem( item => ({
        ...defaultItem,
        model: {}
      }));
      setUnsaved(false);
    }
  }, [isNew, id, load]);

  /**
   * callback if a field in the form gets changed
   *
   * @param {any} field the name of the changed field
   * @param {any} value the new value of the changed field
   */
  const onChangeField = useCallback(
    (field, value) => {
      if (typeof field === 'string') {
        setItem({
          ...item,
          [field]: value,
        });
      } else {
        if (field.length > 2) {
          setItem({
            ...item,
            [field[0]]: {
              ...item[field[0]],
              [field[1]]: {
                ...item[field[1]],
                [field[2]]: value,
              },
            },
          });
        } else {
          setItem({
            ...item,
            [field[0]]: {
              ...item[field[0]],
              [field[1]]: value,
            },
          });
        }
      }

      setUnsaved(true);
    },
    [item, setItem]
  );

  const onRequestClose = () => {
    if (unsaved === true) {
      confirmClosingUnsavedForm(() => dispatch(hideForm()));
    } else {
      dispatch(hideForm());
    }
  };

  const onSaveRequest = () => {
    if (unsaved === true) {
      updateLocation({id: item.model.ID, form: item}).unwrap()
        .then((result) => {
          setItem(result.data);
          dispatch(showToast({kind: "success", title: "Änderungen gespeichert", caption: "Standort wurde gespeichert."}));
          setUnsaved(false);
        })
        .catch((error) => {

        });
    } else {
      dispatch(hideForm());
    }
  };

  const remove = (location) => {
    confirmRemoveLocation({ location })
  };

  let tabs;

  if (isLoading === true || !item || !item.model) {
    tabs = (
      <Tabs type="container">
        <TabList
          aria-label="Tab list"
          contained
        >
          <Tab id="tab_default">
            Allgemein
          </Tab>
          <Tab id="tab_details">
            Kontakt
          </Tab>
          <Tab id="tab_address">
            Erweitert
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="">
                <TextInputSkeleton />
              </FormGroup>
              <FormGroup legendText="">
                <TextAreaSkeleton />
              </FormGroup>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content">
          </TabPanel>
          <TabPanel className="tab-content">
          </TabPanel>
        </TabPanels>
      </Tabs>
    );
  } else {
    tabs = (
      <Tabs type="container">
        <TabList
          aria-label="Tab list"
          contained
        >
          <Tab id="tab_default">
            Allgemein
          </Tab>
          <Tab id="tab_details">
            Kontakt
          </Tab>
          <Tab id="tab_address">
            Erweitert
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="tab-content">
            <Form>
              <FormGroup legendText="">
                <TextInput
                  labelText="Name"
                  autoComplete="off"
                  value={item.name}
                  id="form_location_name"
                  invalid={item.name.length < 4}
                  invalidText="Sie müssen dem Standort einen Namen geben"
                  onChange={(e) => onChangeField('name', e.target.value)}
                />
              </FormGroup>

              <FormGroup legendText="Adresse">
                <TextInput
                  labelText="Straße"
                  autoComplete="off"
                  value={item.address.street}
                  id="form_location_address_street"
                  invalid={has_error(20, error)}
                  invalidText={has_error(20, error)}
                  onChange={(e) =>
                    onChangeField(['address', 'street'], e.target.value)
                  }
                />

                <Row>
                  <Column sm={3} md={3} lg={3}>
                    <TextInput
                      labelText="PLZ"
                      autoComplete="off"
                      value={item.address.postcode}
                      id="form_location_address_postcode"
                      invalid={has_error(21, error)}
                      invalidText={has_error(21, error)}
                      onChange={(e) =>
                        onChangeField(['address', 'postcode'], e.target.value)
                      }
                    />
                  </Column>
                  <Column>
                    <TextInput
                      labelText="Ort"
                      autoComplete="off"
                      value={item.address.city}
                      id="form_location_address_city"
                      invalid={has_error(22, error, 'locations')}
                      invalidText={has_error(22, error, 'locations')}
                      onChange={(e) =>
                        onChangeField(['address', 'city'], e.target.value)
                      }
                    />
                  </Column>
                </Row>
              </FormGroup>
            </Form>
          </TabPanel>
          <TabPanel className="tab-content" >
            <Form>
              <FormGroup legendText="Kontakt">
                <TextInput
                  labelText="E-Mail Adresse"
                  autoComplete="off"
                  value={item.mail}
                  id="form_location_mail"
                  onChange={(e) => onChangeField('mail', e.target.value)}
                />
                <TextInput
                  labelText="Telefonnummer"
                  autoComplete="off"
                  value={item.phone_number}
                  id="form_location_phone_number"
                  onChange={(e) => onChangeField('phone_number', e.target.value)}
                />
              </FormGroup>
              <FormGroup legendText="Technischer Ansprechpartner">
                <br/>
                <CustomSelect
                  isLoading={loadingContacts}
                  selection={item?.technical_contact_id ? [item.technical_contact_id] : []}
                  items={(contacts || []).map(e => ({value: e.model.ID, label: `${e.firstname} ${e.lastname} - ${e.mail}`}))}
                  onSelectionChange={(newSelection) => {console.info("newSelection:", newSelection); onChangeField('technical_contact_id', newSelection.length === 1 ? newSelection[0] : null)}}
                />
                <Button onClick={() => contactForm() } size="sm" kind="secondary">Kontakt anlegen</Button>
                <br/>
                <Toggle
                  id="form_device_settings_show_clock"
                  labelText="Benachrichtigungen Per E-Mail"
                  labelA="Keine Benachrichtigungen bei Störungen"
                  labelB="Automatischer E-Mail Versand bei Störungen"
                  onToggle={(t, _i) => {
                    onChangeField('technical_receive_notifications', t === true ? true : false);
                  }}
                  toggled={item?.technical_receive_notifications}
                />
              </FormGroup>
            </Form>
          </TabPanel>

          <TabPanel className="tab-content">
            <Form>

              {isAdmin &&
                <FormGroup legendText="Administrator Einstellungen">

                </FormGroup>
              }

              <Link href="#" onClick={() => remove(item)}>
                Standort löschen
              </Link>
            </Form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    );
  }

  return (
    <SideForm
      ref={sideform_ref}
      title="Standort Einstellungen"
      subTitle="ausgewählten Standort bearbeiten"
      onClose={onRequestClose}
      onSubmit={onSaveRequest}
      primaryButton={'Speichern'}
      primaryButtonDisabled={!unsaved}
      secondaryButton={unsaved === true ? 'Abbruch' : 'Verlassen'}>
      {isSaving &&  <LoadingPage />}
      <SingleLocationMap lat={item?.address?.lat} lng={item?.address?.lng} />
      <br />
      {tabs}
    </SideForm>
  );
};

export default LocationForm;
